import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
} from "@heroui/react";
import {
  CalendarIcon,
} from "../../components/admin/DashboardIcons.jsx";
import { Tabs, Tab, Spinner } from "@heroui/react";

import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";

// Reusable stat card
const StatCard = ({ label, value }) => (
  <div className="flex-none mb-1 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-100 p-4 h-24 w-auto min-w-[170px] flex flex-col justify-center items-center text-center hover:shadow-lg transition-shadow">
    <p className="text-sm font-semibold text-gray-700 mb-1 uppercase tracking-wide">
      {label}
    </p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const ApexChart = ({ betCountGraph = [] }) => {
  if (!Array.isArray(betCountGraph) || betCountGraph.length === 0)
    return <div>No data found</div>;

  const values = betCountGraph.map((item) => Number(item?.bets) || 0);
  const categories = betCountGraph.map((item) => item?.name || "");

  const series = [
    {
      name: "Bets",
      data: values,
    },
  ];

  const options = {
    chart: {
      id: "bets-bar",
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 6,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Number(val).toFixed(0)}`,
      style: { fontSize: "10px" },
    },
    xaxis: {
      categories,
      labels: { rotate: -45 },
    },
    yaxis: { min: 0 },
    grid: { show: false },
    colors: ["#f59e0b"],
    tooltip: {
      shared: false,
      y: { formatter: (val) => `${Number(val).toFixed(0)}` },
    },
    legend: { show: false },
    title: {
      text: "Bets",
      align: "left",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
  };

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div style={{ width: `${Math.max(values.length * 80, 300)}px` }}>
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height={350}
          width={values.length * 80}
        />
      </div>
    </div>
  );
};

const TurnoverBar = ({ data }) => {
  const arrayData = Array.isArray(data)
    ? data
    : data
    ? Object.values(data)
    : [];

  if (!arrayData.length) return <div>No data found</div>;

  const matchName = arrayData.map((item) =>
    (item.name ?? item.label ?? "").trim()
  );
  const turnover = arrayData.map((item) =>
    Number(item.turnOver ?? item.value ?? 0)
  );

  const options = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "70%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toFixed(2),
      offsetY: -20,
      style: { fontSize: "12px", colors: ["#304758"] },
    },
    grid: { show: false },
    xaxis: { categories: matchName },
    yaxis: { labels: { show: true }, min: 0 },
    colors: ["#6366f1"],
    title: { text: "Turnover", align: "left" },
  };

  const chartWidth = matchName.length * 120;

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div style={{ width: `${chartWidth}px` }}>
        <ReactApexChart
          options={options}
          series={[{ name: "Turnover", data: turnover }]}
          type="bar"
          height={300}
          width={chartWidth}
        />
      </div>
    </div>
  );
};

// GGR (Line) 0..11
const GGRLine = ({ data = [] }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <div>No data found</div>;
  }

  //  Filter invalid items
  const cleanedData = data
    .filter((item) => item && item.matchName && !isNaN(Number(item.GGR)))
    .map((item) => ({
      x: item.matchName || "Unknown",
      y: Number(item.GGR) || 0,
    }));

  if (cleanedData.length === 0) {
    return <div>No valid data</div>;
  }

  const yValues = cleanedData.map((d) => d.y);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);

  const series = [{ name: "GGR", data: cleanedData }];

  const options = {
    chart: { type: "area", toolbar: { show: false } },
    stroke: { width: 2, curve: "straight", colors: ["#f4a724"] },
    dataLabels: {
      enabled: true,
      formatter: (v) => v.toFixed(1),
      style: { fontSize: "11px" },
      offsetY: -10,
    },
    markers: { size: 4 },
    fill: { type: "solid", colors: ["#f4a724"], opacity: 0.25 },
    grid: { show: false },
    xaxis: {
      type: "category",
      labels: { rotate: -60, style: { fontSize: "12px" } },
    },
    yaxis: {
      min: yMin - 5 < 0 ? 0 : yMin - 5,
      max: yMax + 5,
    },
    title: { text: "GGR", align: "left" },
    tooltip: { y: { formatter: (v) => v.toFixed(1) } },
    annotations: { yaxis: [{ y: 0, borderColor: "#d1d5db" }] },
  };

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: `${Math.max(cleanedData.length * 80, 300)}px` }}>
        <ReactApexChart
          options={options}
          series={series}
          type="area"
          height={300}
        />
      </div>
    </div>
  );
};

// Turnover by Game (Pie)
const TurnoverByGamePie = ({ data = [] }) => {
  if (!data || !data.length) return <div>No data found</div>;
  // Normalize API response
  const formattedData = data.map((item) => {
    const num = parseFloat(item.turnOver ?? item.value ?? 0); // <-- FIX HERE
    return {
      name: item.name ?? "Unknown",
      turnOver: isNaN(num) ? 0 : num,
    };
  });

  // Remove items with 0 turnover (optional)
  const nonZeroData = formattedData.filter((item) => item.turnOver > 0);

  if (!nonZeroData.length) return <div>No data found</div>;

  const total = nonZeroData.reduce((sum, item) => sum + item.turnOver, 0);
  const threshold = total * 0.02;

  let filteredData = [];
  let othersValue = 0;

  nonZeroData.forEach((item) => {
    if (item.turnOver < threshold) othersValue += item.turnOver;
    else filteredData.push(item);
  });

  if (othersValue > 0)
    filteredData.push({ name: "Others", turnOver: othersValue });

  const labels = filteredData.map((item) => item.name);
  const series = filteredData.map((item) => Number(item.turnOver));
  const options = {
    chart: { type: "pie", toolbar: { show: false } },
    labels,
    legend: { position: "right", fontSize: "10px" },
    dataLabels: {
      enabled: true,
      dataLabels: {
        enabled: true,
        formatter: (val) => val.toFixed(1),
      },
    },

    tooltip: { y: { formatter: (v) => `${v.toFixed(2)}` } },
    colors: ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#f472b6"],
    title: { text: "Turnover by Game", align: "left" },
  };

  return (
    <div
      className="w-full"
      style={{ minWidth: 300, maxWidth: 500, margin: "0 auto" }}
    >
      <ReactApexChart
        key={JSON.stringify(series)} // force chart to re-render when series changes
        options={options}
        series={series}
        type="pie"
        height={300}
      />
    </div>
  );
};

const UsersByOperator = ({ data }) => {
  const [chartData, setChartData] = useState({ operators: [], values: [] });
  const [isNoData, setIsNoData] = useState(false);

  useEffect(() => {
    // Normalize data array for all API response formats
    const validArray = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];

    if (validArray.length === 0) {
      setChartData({ operators: [], values: [] });
      setIsNoData(true);
      return;
    }

    const operators = validArray.map((item) => item.operator || "Operator");
    const values = validArray.map(
      (item) => item.unique_users ?? item.uniqe_users ?? 0
    );

    if (values.every((v) => v === 0)) {
      setChartData({ operators: [], values: [] });
      setIsNoData(true);
    } else {
      setChartData({ operators, values });
      setIsNoData(false);
    }
  }, [data]);

  const maxValue = Math.max(...chartData.values, 1) * 1.2;
  const innerHeight = Math.max(chartData.operators.length * 60, 300);

  const options = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: "10%" },
    },
    dataLabels: { enabled: false },
    grid: { show: false },
    xaxis: { categories: chartData.operators, min: 0, max: maxValue },
    yaxis: { labels: { show: true } },
    colors: ["#10b981"],
    title: { text: "Unique Users by Operator", align: "left" },
  };

  return (
    <div
      style={{ height: innerHeight }}
      className="overflow-y-auto overflow-x-hidden"
    >
      {isNoData ? (
        <div className="text-center mt-4 text-gray-500">No data found</div>
      ) : (
        <ReactApexChart
          options={options}
          series={[{ name: "Users", data: chartData.values }]}
          type="bar"
          height={innerHeight}
        />
      )}
    </div>
  );
};

// Turnover by Operator (Column)
const TurnoverByOperator = ({ data }) => {
  if (!data) return <div>No data found</div>;

  // Normalize to array
  const arrayData = Array.isArray(data) ? data : [data];

  if (!arrayData.length) return <div>No data found</div>;

  const labels = arrayData.map((item) => item.operator_id || "Unknown");
  const turnover = arrayData.map((item) => Number(item.turn_over || 0));

  const options = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        columnWidth: "10%",
        borderRadius: 2,
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: { fontSize: "11px", colors: ["#304758"] },
    },
    grid: { show: false },
    xaxis: { categories: labels, labels: { rotate: -90, rotateAlways: true } },
    yaxis: { labels: { show: true }, min: 0 },
    colors: ["#3b82f6"],
    title: { text: "Turnover by Operator", align: "left" },
    tooltip: { y: { formatter: (v) => `${v.toFixed(2)}` } },
  };

  const chartWidth = Math.max(labels.length * 80, 300);

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div style={{ width: `${chartWidth}px` }}>
        <ReactApexChart
          options={options}
          series={[{ name: "Turnover", data: turnover }]}
          type="bar"
          height={300}
          width={chartWidth}
        />
      </div>
    </div>
  );
};

const ActiveUsersScatter = ({ activeUser }) => {
  // Handle both single and multiple data responses
  const rawData =
    activeUser?.result?.data || // single data response safely
    activeUser?.data || // single data response
    activeUser?.data ||
    [][0] ||
    activeUser?.[0] || // single data response as array
    [];

  const dataArray = Array.isArray(rawData) ? rawData : [rawData];

  const operators = dataArray.map((item) => item.operator);
  const values = dataArray.map((item) => Number(item.unique_users));

  if (!operators.length) {
    return <div className="text-gray-500 text-sm p-2">No data found</div>;
  }

  const series = [{ name: "Users", data: values }];

  const options = {
    chart: { type: "area", toolbar: { show: false } },
    xaxis: {
      type: "category",
      categories: operators,
      labels: { rotate: -45, style: { fontSize: "10px" } },
      title: { text: "Operators" },
    },
    yaxis: {
      min: 0,
      title: { text: "Unique Users" },
      labels: { style: { fontSize: "10px" } },
    },
    stroke: { curve: "smooth", width: 2, colors: ["#22c55e"], dashArray: 3 },
    fill: { type: "solid", colors: ["#22c55e"], opacity: 0.25 },
    markers: { size: 4, colors: ["#22c55e"], strokeWidth: 2 },
    dataLabels: { enabled: true, formatter: (val) => `${val}` },
    colors: ["#22c55e"],
    title: { text: "Active Users", align: "left", style: { fontSize: "14px" } },
    tooltip: { y: { formatter: (v) => `${v} user${v !== 1 ? "s" : ""}` } },
    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
  };

  const chartWidth = Math.max(operators.length * 120, 600);

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div style={{ width: `${chartWidth}px` }}>
        <ReactApexChart
          options={options}
          series={series}
          type="area"
          height={300}
          width={chartWidth}
        />
      </div>
    </div>
  );
};

const Metric = () => {
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState("");
  // const [selectedMatchName, setselectedMatchName] = useState("all");
  const [betCount, setBetCount] = useState("");
  const [turnOver, setTurnOver] = useState("");
  const [wonAmount, setWonAmount] = useState("");
  const [GGRAmount, setGGRAmount] = useState("");
  const [GGRPer, setGGRPer] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [chartData, setChartData] = useState({ operators: [], values: [] });
  const [betCountGraph, setBetCountGraph] = useState();
  const [turnOverGraph, setTurnOverGraph] = useState();
  const [turnOverOperator, setTurnOverOperator] = useState();
  const [uniqueUserOperator, setUniqueUserOperator] = useState();
  const [activeUser, setActiveUser] = useState();
  const [ggrData, setGgrData] = useState([]);
  const [markets, setMarkets] = useState([]); // all markets
  const [filteredMarkets, setFilteredMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState("all");
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("all");
  const [statusList, setStatusList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [usersDetails, setUsersDetails] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [matchDetails, setMatchDetails] = useState([]);
  const [marketSearchValue, setMarketSearchValue] = useState("");
  const [matchSearchValue, setMatchSearchValue] = useState("");
  const [isMatchDropdownOpen, setIsMatchDropdownOpen] = useState(false);
  const [userSearchValue, setUserSearchValue] = useState("");
  const [selected, setSelected] = React.useState("winners");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingPie, setLoadingPie] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isUserDropdownEnabled, setIsUserDropdownEnabled] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const dropdownRef = useRef(null);

  const hasUsers = Array.isArray(users) && users.length > 0;
  const [selectedRange, setSelectedRange] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // const getRangeDates = useCallback(
  //   (rangeKey) => {
  //     const now = new Date();
  //     const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  //     const toYmd = (d) => {
  //       const y = d.getFullYear();
  //       const m = String(d.getMonth() + 1).padStart(2, "0");
  //       const day = String(d.getDate()).padStart(2, "0");
  //       return `${y}-${m}-${day}`;
  //     };

  //     switch (rangeKey) {
  //       case "today":
  //         return { start: toYmd(end), end: toYmd(end) };
  //       case "yesterday": {
  //         const yest = new Date(end);
  //         yest.setDate(end.getDate() - 1);
  //         return { start: toYmd(yest), end: toYmd(yest) };
  //       }
  //       case "last7": {
  //         const s = new Date(end);
  //         s.setDate(end.getDate() - 6);
  //         return { start: toYmd(s), end: toYmd(end) };
  //       }
  //       case "thisMonth": {
  //         const s = new Date(end.getFullYear(), end.getMonth(), 1);
  //         return { start: toYmd(s), end: toYmd(end) };
  //       }
  //       case "last30": {
  //         const s = new Date(end);
  //         s.setDate(end.getDate() - 29);
  //         return { start: toYmd(s), end: toYmd(end) };
  //       }
  //       case "custom":
  //         return { start: customStart || "", end: customEnd || "" };
  //       default:
  //         return { start: toYmd(end), end: toYmd(end) };
  //     }
  //   },
  //   [customStart, customEnd]
  // );

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const range = getRangeDates(selectedRange);

  //     // For custom range: must have both dates
  //     if (selectedRange === "custom" && (!range.start || !range.end)) {
  //       return;
  //     }

  //     const payload = {
  //       start_date: range.start,
  //       end_date: range.end,
  //       range: selectedRange,
  //     };

  //     try {
  //       const res = await fetch(
  //         `${process.env.REACT_APP_API_HOST}/matches/filter`,
  //         {
  //           method: "POST",
  //           headers: {
  //             "Content-Type": "application/json",
  //           },
  //           body: JSON.stringify(payload),
  //         }
  //       );

  //       const data = await res.json();

  //       if (data.success) {
  //         console.log("Filtered Matches:", data.data);
  //         // setFilteredMatches(data.data);  // <-- optional state update
  //       } else {
  //         console.warn("No matches found");
  //       }
  //     } catch (err) {
  //       console.error("Error fetching matches:", err);
  //     }
  //   };

  //   fetchData();
  // }, [selectedRange, customStart, customEnd, getRangeDates]);

  const getSelectedMatchLabel = useCallback(() => {
    if (
      Array.isArray(matches) &&
      matches.length === 1 &&
      matches[0]?.id === "no-match"
    ) {
      return "No Matches";
    }

    if (!selectedMatch) {
      return matches && matches.length > 0 ? "" : "";
    }

    if (!Array.isArray(matches) || matches.length === 0) {
      if (selectedMatch === "all") return "All Matches";
      if (selectedMatch === "no-match") return "No Matches";
      return "";
    }

    const selectedMatchObj = matches.find(
      (match) => String(match.id) === String(selectedMatch)
    );

    if (!selectedMatchObj) {
      if (selectedMatch === "all") return "All Matches";
      if (selectedMatch === "no-match") return "No Matches";
      return "";
    }

    if (selectedMatchObj.id === "all") return "All Matches";
    if (selectedMatchObj.id === "no-match") return "No Matches";

    return selectedMatchObj.name?.toString() || "";
  }, [matches, selectedMatch]);

  const handleSelectAll = () => {
    if (hasUsers) setSelectedUsers(users.map((u) => u.user_id));
  };

  const handleDeselectAll = async () => {
    if (!users || users.length === 0) return;

    // Clear selectedUsers array to uncheck all
    setSelectedUsers([]);

    // Call metrics for "all users" with default filters
    await handleUserChange({
      target: { value: "all" }, // we still pass "all" to show metrics for all users
      extraFilters: {
        market_id: "all",
        match_id: "all",
        draw_date: selectedDate,
        operator_id: "all",
      },
    });
  };
  const toggleUser = async (id) => {
    let newSelectedUsers;

    if (selectedUsers.includes(id)) {
      // Remove user
      newSelectedUsers = selectedUsers.filter((uid) => uid !== id);
    } else {
      // Add user
      newSelectedUsers = [...selectedUsers, id];
    }

    setSelectedUsers(newSelectedUsers);

    // Prepare value to pass to API
    const apiValue =
      newSelectedUsers.length === 0 || newSelectedUsers.length === users.length
        ? ["all"] // pass "all" if none or all are selected
        : newSelectedUsers;

    // Call handleUserChange with the correct value
    await handleUserChange({ target: { value: apiValue } });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredUserList = users.filter((u) => {
    const name = u.name || u.user_id;
    return (
      u.user_id !== "all" &&
      u.user_id !== "etc" &&
      u.user_id !== "no-user" &&
      name.toLowerCase().includes(userSearchValue.toLowerCase())
    );
  });

  // Sorting states for different tables
  const [sortConfig, setSortConfig] = useState({
    turnoverGames: { key: null, direction: "asc" },
    turnoverUsers: { key: null, direction: "asc" },
    winners: { key: null, direction: "asc" },
    contributors: { key: null, direction: "asc" },
  });

  const fetchActiveUsers = async (date) => {
    if (!date) return;

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const requestOptions = {
      method: "POST", // changed from GET to POST
      headers,
      body: JSON.stringify({ draw_date: date }), // send date in JSON body
    };

    const url = process.env.REACT_APP_API_HOST + "/metric-activeusers-graph";

    try {
      const response = await fetch(url, requestOptions);
      if (response.status === 401) return null;

      const result = await response.json();
      setActiveUser({ result });
    } catch (err) {
      console.log("Error fetching active users:", err);
    }
  };

  useEffect(() => {
    fetchActiveUsers(selectedDate);
  }, [selectedDate]);

  //dateDropdown change
  const handleDateChange = async (e) => {
    const date = e.target.value;
    if (date === selectedDate) return;

    setLoading(true);
    setSelectedDate(date);

    // Reset dropdowns & metrics
    setSelectedMarket("all");
    setSelectedMatch("all");
    setSelectedOperator("all");
    setSelectedUser("all");

    setMarkets([]);
    setMatches([]);
    setOperators([]);
    setTurnOver(0);
    setUniqueUsers(0);
    setBetCount(0);
    setWonAmount(0);
    setGGRAmount(0);
    setGGRPer(0);

    try {
      const headers = new Headers({
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("jwt"),
      });

      // 1️⃣ Fetch markets
      const marketRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/market-metric?draw_date=${date}`,
        { headers }
      );
      const marketData = await marketRes.json();
      const marketList = marketData?.data || [];

      setMarkets(
        marketList.length > 0
          ? [{ id: "all", name: "All Markets" }, ...marketList]
          : [{ id: "no-market", name: "No Market" }]
      );
      const selectedMarketId = marketList.length > 0 ? "all" : "no-market";
      setSelectedMarket(selectedMarketId);

      // 2️⃣ Fetch matches
      const matchRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/match-metric?draw_date=${date}`,
        { headers }
      );
      const matchData = await matchRes.json();
      const matchesList = matchData?.data || [];

      setMatches(
        matchesList.length > 0
          ? [{ id: "all", name: "All Matches" }, ...matchesList]
          : [{ id: "no-match", name: "No Matches" }]
      );
      const selectedMatchId = matchesList.length > 0 ? "all" : "no-match";
      setSelectedMatch(selectedMatchId);

      // 3️⃣ Fetch operators
      const operatorRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/operator-metric?draw_date=${date}`,
        { headers }
      );
      const operatorData = await operatorRes.json();
      const operatorListFromApi = operatorData?.data || [];

      // Map API response to consistent format
      const operatorList =
        operatorListFromApi.length > 0
          ? [
              { id: "all", name: "All Operators" }, // prepend "All Operators"
              ...operatorListFromApi.map((op) => ({
                id: op.id,
                name: op.operator_id,
              })),
            ]
          : [{ id: "no-operator", name: "No Operators" }];

      // Set operators state
      setOperators(operatorList);

      // Set selectedOperator default
      if (operatorListFromApi.length > 0) {
        setSelectedOperator("all"); // Only set "all" if real data exists
      } else {
        setSelectedOperator("no-operator"); // Otherwise, set "no-operator"
      }

      // 4️⃣ Fetch users
      const userRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/user-metric`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify({ draw_date: date }),
        }
      );

      const userData = await userRes.json();
      const userListFromApi = userData?.data || [];

      const hasUsers = userListFromApi.length > 0;

      if (!hasUsers) {
        setUsers([{ user_id: "no-user", name: "No Users" }]);
        setSelectedUsers([]);
        setSelectedUser("no-user");

        // Do NOT return → continue to fetch other data if needed
      } else {
        const usersArr = [
          { user_id: "all", name: "All Users" },
          ...userListFromApi.map((u) => ({
            user_id: u.user_id,
            name: u.username || u.user_id,
          })),
        ];

        setUsers(usersArr);
        setSelectedUsers(
          usersArr.filter((u) => u.user_id !== "all").map((u) => u.user_id)
        );
        setSelectedUser("all");
      }

      // 4️⃣ Build query using fetched data
      const query = buildQuery({
        draw_date: date,
        match_id: matchesList.length > 0 ? selectedMatchId : undefined,
        market_id: marketList.length > 0 ? selectedMarketId : undefined,
        operator_id: operatorList.length > 0 ? "all" : undefined,
      });

      // 5️⃣ Fetch metrics in parallel
      const [
        uniqueUserBetRes,
        turnoverRes,
        ggrRes,
        betGraphRes,
        turnoverGraphRes,
        activeUsersRes,
        turnoverOperatorRes,
        uniqueOperatorRes,
        statusRes,
      ] = await Promise.all([
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-users-betcount`,
          body: {
            draw_date: date,
            match_id: selectedMatchId,
            operator_id: "all",
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-count`,
          body: {
            draw_date: date,
            match_id: selectedMatchId,
            operator_id: "all",
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/grr-amount`,
          body: {
            draw_date: date,
            match_id: selectedMatchId,
            operator_id: "all",
            user_id: "all",
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-bet-graph`,
          body: {
            bet_date: date,
            match_id: selectedMatchId,
            operator_id: "all",
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`,
          body: { date, match_id: selectedMatchId, operator_id: "all" },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-activeusers-graph?draw_date=${date}`,
          body: {
            draw_date: date,
            match_id: selectedMatchId,
            operator_id: "all",
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`,
          body: { date, operator_id: "all" },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          body: { date, operator_id: "all" },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/status-metric`,
          body: { draw_date: date },
        }),
      ]);

      setUniqueUsers(uniqueUserBetRes?.data?.unique_users || 0);
      setBetCount(uniqueUserBetRes?.data?.bet_count || 0);
      setTurnOver(turnoverRes?.data?.turn_over || 0);
      setGGRAmount(ggrRes?.data?.ggr_amount || 0);
      setGGRPer(ggrRes?.data?.ggr_percentage || 0);

      setBetCountGraph(betGraphRes || []);
      setTurnOverGraph(turnoverGraphRes || []);
      setActiveUser(activeUsersRes || {});
      setTurnOverOperator(turnoverOperatorRes || []);
      setUniqueUserOperator(uniqueOperatorRes || []);
      const statusData = statusRes?.data || [];

      const uniqueStatuses = [
        ...new Set(
          statusData
            .map((item) => item.status?.data?.[0] ?? null)
            .filter((v) => v !== null)
        ),
      ];

      setStatusList(uniqueStatuses);

      // ---- FIXED LOGIC ----
      if (uniqueStatuses.length === 0) {
        // No status → "no-status"
        setSelectedStatus("no-status");
      } else {
        // Status exists → validate current selection

        const current = Number(selectedStatus);

        // If selectedStatus is NOT valid → reset to "all"
        if (isNaN(current) || !uniqueStatuses.includes(current)) {
          setSelectedStatus("all");
        }
      }

      await fetchWonAmount(date);
    } catch (err) {
      console.error("❌ Error fetching metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (matches && matches.length > 0) {
      // Only set "all" if no match is currently selected
      setSelectedMatch((prev) => prev || "all");
    }
  }, [matches]);

  // Helper to build API query
  const buildQuery = (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) query.append(k, v);
    });
    return query.toString();
  };

  // Market change
  const handleMarketChange = (e) => {
    const marketId = e.target ? e.target.value : e;
    fetchMarketMetrics(marketId);
  };

  const fetchMarketMetrics = async (marketId) => {
    try {
      // ⭐ If All Markets selected → Reset everything
      if (marketId === "all") {
        setSelectedMatch("all");
        setSelectedOperator("all");
        setSelectedUser("all");
        setSelectedUsers([]);
        setSelectedStatus("all");

        setMatches([{ id: "all", name: "All Matches" }]);
        setOperators([{ id: "all", name: "All Operators" }]);
        setFilteredUsers([{ user_id: "all", name: "All Users" }]);
        setStatusList([]);

        // Fetch full metrics
        await fetchMetrics({
          marketId: "all",
          matchId: "all",
          operatorId: "all",
          userId: "all",
          status: "all",
        });

        return; // stop here (do not load market-specific data)
      }

      setSelectedMarket(marketId);
      resetMetrics();

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      };

      // 1️⃣ Fetch matches
      const matchRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/match-metric?market_id=${marketId}&draw_date=${selectedDate}`,
        { headers }
      );
      const matchesFromApi = (await matchRes.json())?.data || [];
      setMatches(
        matchesFromApi.length
          ? [{ id: "all", name: "All Matches" }, ...matchesFromApi]
          : [{ id: "no-match", name: "No Matches" }]
      );

      // 2️⃣ Fetch operators
      const opRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/operator-metric?market_id=${marketId}&draw_date=${selectedDate}`,
        { headers }
      );
      const operatorsFromApi = (await opRes.json())?.data || [];
      setOperators(
        operatorsFromApi.length
          ? [
              { id: "all", name: "All Operators" },
              ...operatorsFromApi.map((op) => ({
                id: op.id,
                name: op.operator_id,
              })),
            ]
          : [{ id: "no-operator", name: "No Operators" }]
      );

      // 3️⃣ Fetch users
      const userRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/user-metric`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            market_id: marketId,
            draw_date: selectedDate,
          }),
        }
      );
      const usersFromApi = (await userRes.json())?.data || [];
      setFilteredUsers(
        usersFromApi.length
          ? [
              { user_id: "all", name: "All Users" },
              ...usersFromApi.map((u) => ({
                user_id: u.user_id,
                name: u.name || u.user_id,
              })),
            ]
          : [{ user_id: "no-user", name: "No Users" }]
      );

      // 4️⃣ Status
      const statusRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/status-metric`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            market_id: marketId,
            draw_date: selectedDate,
            match_id: "all",
            operator_id: "all",
            user_id: "all",
          }),
        }
      );
      const statusData = (await statusRes.json())?.data || [];
      const uniqueStatuses = Array.from(
        new Set(
          statusData.flatMap((item) =>
            Array.isArray(item.status?.data) ? item.status.data : []
          )
        )
      );
      setStatusList(uniqueStatuses);
      setSelectedStatus(uniqueStatuses.length ? "all" : "no-status");

      // Match
      if (!matchesFromApi.some((m) => String(m.id) === String(selectedMatch))) {
        setSelectedMatch("all");
      }

      // Operator
      if (
        !operatorsFromApi.some(
          (op) => String(op.id) === String(selectedOperator)
        )
      ) {
        setSelectedOperator("all");
        setSelectedUser("all"); // user depends on operator
      }

      // User
      if (!usersFromApi.some((u) => u.user_id === selectedUser)) {
        setSelectedUser("all");
      }

      // Status
      if (!uniqueStatuses.includes(selectedStatus)) {
        setSelectedStatus("all");
      }

      // 5️⃣ Fetch main metrics with corrected filters
      await fetchMetrics({
        marketId,
        matchId: selectedMatch,
        operatorId: selectedOperator,
        userId: selectedUser,
        status: selectedStatus,
      });
    } catch (err) {
      console.error("Error fetching metrics for market:", err);
    }
  };

  const handleMatchChange = (e) => {
    const matchId = e.target ? e.target.value : e;

    // When user selects "All Matches" → Reset everything
    if (matchId === "all") {
      setSelectedMatch("all");
      setSelectedOperator("all");
      setSelectedUser("all");
      setSelectedMarket("all");
      setSelectedStatus("all");
      resetMetrics();

      // Fetch all metrics with skipValidation = true
      fetchMatchMetrics("all", true, true);
      return;
    }

    // Fetch metrics for a specific match
    fetchMatchMetrics(matchId, false, false);
  };

  const fetchMatchMetrics = async (
    matchId,
    isFromInitial = false,
    skipValidation = false
  ) => {
    try {
      // 0️ Reset metrics
      setSelectedMatch(matchId);
      if (!isFromInitial) resetMetrics();

      const finalMatchId = matchId;

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      };

      // 1️ Fetch markets for the match
      const marketRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/market-metric?match_id=${finalMatchId}&draw_date=${selectedDate}`,
        { headers }
      );
      const marketsFromApi = (await marketRes.json())?.data || [];

      // Reset or validate market
      if (!skipValidation) {
        if (
          !marketsFromApi.some((m) => String(m.id) === String(selectedMarket))
        ) {
          setSelectedMarket("all");
        }
      } else {
        setSelectedMarket("all");
      }

      //  Fetch operators for the selected match & market
      const opRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/operator-metric?${buildQuery({
          market_id: selectedMarket,
          match_id: finalMatchId,
          draw_date: selectedDate,
        })}`,
        { headers }
      );
      const operatorsFromApi = (await opRes.json())?.data || [];
      const operatorList =
        operatorsFromApi.length > 0
          ? [
              { id: "all", name: "All Operators" },
              ...operatorsFromApi.map((op) => ({
                id: op.id,
                name: op.operator_id,
              })),
            ]
          : [{ id: "no-operator", name: "No Operators" }];
      setOperators(operatorList);

      // Reset or validate operator
      if (!skipValidation) {
        if (
          !operatorsFromApi.some(
            (op) => String(op.id) === String(selectedOperator)
          )
        ) {
          setSelectedOperator("all");
          setSelectedUser("all"); // reset user if operator reset
        }
      } else {
        setSelectedOperator("all");
        setSelectedUser("all");
      }

      //  Fetch statuses
      const statusRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/status-metric`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            market_id: selectedMarket,
            draw_date: selectedDate,
            match_id: finalMatchId,
            operator_id: selectedOperator || "all",
            user_id: selectedUser || "all",
          }),
        }
      );
      const statusData = (await statusRes.json())?.data || [];
      const uniqueStatuses = Array.from(
        new Set(
          statusData.flatMap((item) =>
            Array.isArray(item.status?.data) ? item.status.data : []
          )
        )
      );
      setStatusList(uniqueStatuses);

      // Reset or validate status
      // Reset or validate status (KEEP IF VALID)
      if (!skipValidation) {
        if (!uniqueStatuses.includes(Number(selectedStatus))) {
          setSelectedStatus("all");
        }
      } else {
        setSelectedStatus("all");
      }

      //  Fetch metrics
      await fetchMetrics({
        marketId: selectedMarket,
        matchId: finalMatchId,
        operatorId: selectedOperator || "all",
        userId: selectedUser || "all",
        status: selectedStatus || "all",
      });
    } catch (err) {
      console.error("Error fetching match metrics:", err);
      resetMetrics();
    } finally {
      setIsInitialLoad(false);
    }
  };

  const handleMatchClick = async (match_id) => {
    setLoadingUsers(true);
    setLoadingPie(true); // show loader for graphs

    try {
      const body = {
        draw_date: selectedDate,
        match_id,
      };

      // Fetch user details for clicked match
      const userRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/metrics-userDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify(body),
        }
      );

      if (!userRes.ok) throw new Error("Failed to fetch user details");
      const userResult = await userRes.json();
      setUsersDetails(userResult.data || []);

      // Prepare body for graph APIs
      const graphBody = {
        draw_date: selectedDate,
        date: selectedDate,
        bet_date: selectedDate,
        market_id: "all",
        match_id,
        operator_id: "all",
        status: "all",
      };

      // Fetch all graphs in parallel
      const [
        betGraphRes,
        turnoverGraphRes,
        ggrRes,
        activeUsersRes,
        turnoverOperatorRes,
        uniqueUsersRes,
        // wonJson
      ] = await Promise.all([
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-bet-graph`,
          body: { ...graphBody, bet_date: selectedDate },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`,
          body: { ...graphBody },
        }),
        fetchGGR({
          draw_date: selectedDate,
          match_id: match_id,
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-activeusers-graph`,
          body: { ...graphBody },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`,
          body: { ...graphBody },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          body: { ...graphBody },
        }),
      ]);
      const jwt = localStorage.getItem("jwt");
      const user_id = null; // fallback, can be selected by user
      const operator_id = null; // fallback
      const status = "all";
      const metricsCalls = [
        fetch(
          `${process.env.REACT_APP_API_HOST}/metric-unique-users-betcount`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              ...graphBody,
              user_id,
              operator_id,
              status,
            }),
          }
        ).then((r) => r.json()),

        fetch(`${process.env.REACT_APP_API_HOST}/metric-turnover-count`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ ...graphBody, user_id, operator_id, status }),
        }).then((r) => r.json()),

        // fetch(`${process.env.REACT_APP_API_HOST}/wonamount`, {
        //   method: "POST",
        //   headers: {
        //     "Content-Type": "application/json",
        //     Authorization: `Bearer ${jwt}`,
        //   },
        //   body: JSON.stringify({ ...graphBody, user_id, operator_id, status }),
        // }).then((r) => r.json()),
      ];
      const [uniqueUserBetRes, turnoverRes, wonJson] = await Promise.all(
        metricsCalls
      );

      // Update states safely
      setUniqueUsers(uniqueUserBetRes?.data?.unique_users || 0);
      setBetCount(uniqueUserBetRes?.data?.bet_count || 0);
      setTurnOver(turnoverRes?.data?.turn_over || 0);
      // setWonAmount(wonJson?.data || 0);

      // Update graph states
      setBetCountGraph(betGraphRes || []);
      setTurnOverGraph(turnoverGraphRes || []);
      setGgrData(ggrRes);
      setActiveUser(activeUsersRes || {});
      setTurnOverOperator(turnoverOperatorRes || []);
      setUniqueUserOperator(uniqueUsersRes || []);
    } catch (err) {
      console.error("Error loading data and graphs:", err);
    } finally {
      setLoadingUsers(false);
      setLoadingPie(false);
    }
  };

  const handleUserClick = (user_id, operator_id) => {
    fetchAllGraphs({
      selectedDate,
      user_id,
      operator_id,
      status:selectedStatus,
    });
      
  };

  // Separate function to fetch all graph data
  const fetchAllGraphs = async ({
    selectedDate,
    user_id,
    operator_id,
    status:selectedStatus,
  }) => {
    try {
      console.log({ selectedDate, user_id, operator_id,  status:selectedStatus });

      setLoadingPie(true);

      const jwt = localStorage.getItem("jwt");

      // ---------- MAIN 6 GRAPH API CALLS ----------
      const mainGraphCalls = [
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-bet-graph`,
          body: { bet_date: selectedDate, user_id, operator_id, status: selectedStatus  },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`,
          body: { date: selectedDate, user_id, operator_id, status: selectedStatus  },
        }),
        fetchGGR({
          draw_date: selectedDate,
          user_id,
          operator_id,
          status: selectedStatus ,
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-activeusers-graph`,
          body: { draw_date: selectedDate, user_id, operator_id, status: selectedStatus  },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`,
          body: { date: selectedDate, user_id, operator_id, status: selectedStatus  },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          body: { draw_date: selectedDate, user_id, operator_id, status: selectedStatus  },
        }),
      ];

      // ---------- METRICS: betCount, turnover, wonAmount ----------
      const metricsCalls = [
        fetch(
          `${process.env.REACT_APP_API_HOST}/metric-unique-users-betcount`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              draw_date: selectedDate,
              user_id,
              operator_id,
              status: selectedStatus ,
            }),
          }
        ).then((r) => r.json()),

        fetch(`${process.env.REACT_APP_API_HOST}/metric-turnover-count`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            draw_date: selectedDate,
            user_id,
            operator_id,
            status: selectedStatus ,
          }),
        }).then((r) => r.json()),

        // fetch(`${process.env.REACT_APP_API_HOST}/wonamount`, {
        //   method: "POST",
        //   headers: {
        //     "Content-Type": "application/json",
        //     Authorization: `Bearer ${jwt}`,
        //   },
        //   body: JSON.stringify({
        //     draw_date: selectedDate,
        //     user_id,
        //     operator_id,
        //     status: selectedStatus ,
        //   }),
        // }).then((r) => r.json()),
      ];

      const [
        betGraphRes,
        turnoverGraphRes,
        ggrRes,
        activeUsersRes,
        turnoverOperatorRes,
        uniqueUsersRes,

        // small metrics
        uniqueUserBetRes,
        turnoverRes,
        // wonJson
      ] = await Promise.all([...mainGraphCalls, ...metricsCalls]);

      // ---------- SET STATES ----------
      setBetCountGraph(betGraphRes || []);
      setTurnOverGraph(turnoverGraphRes || []);
      setGgrData(ggrRes || []);
      setActiveUser(activeUsersRes || {});
      setTurnOverOperator(turnoverOperatorRes || []);
      setUniqueUserOperator(uniqueUsersRes || []);

      // small metrics
      setUniqueUsers(uniqueUserBetRes?.data?.unique_users || 0);
      setBetCount(uniqueUserBetRes?.data?.bet_count || 0);
      // setWonAmount(wonJson?.data || 0);
      setTurnOver(turnoverRes?.data?.turn_over || 0);
    } catch (err) {
      console.error("Error fetching graphs:", err);
    } finally {
      setLoadingPie(false);
    }
  };

  // Reusable function to fetch GGR data
  const fetchGGR = async ({
    draw_date,
    market_id,
    match_id,
    operator_id,
    user_id,
    status,
  }) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
          draw_date,
          market_id,
          match_id,
          operator_id,
          user_id,
          status,
        }),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/grr-amount`,
        requestOptions
      );

      if (!response.ok) {
        console.error("Failed to fetch GGR:", response.statusText);
        return [];
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (err) {
      console.error("Error fetching GGR:", err);
      return [];
    }
  };

  const handleOperatorChange = async (e) => {
    const operatorId = e.target.value;
    setSelectedOperator(operatorId);
    setIsUserDropdownEnabled(operatorId !== "all" && operatorId !== "");

    // Reset metrics
    resetMetrics();

    // setLoading(true);
    let matchIdToUse = selectedMatch;
    let marketIdToUse = selectedMarket;

    try {
      //  Fetch matches for this operator
      const matchRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/match-metric?operator_id=${operatorId}&draw_date=${selectedDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      const matchJson = await matchRes.json();
      const matchList = matchJson?.data || [];
      setMatches([{ id: "all", name: "All Matches" }, ...matchList]);

      if (!matchList.find((m) => String(m.id) === String(selectedMatch))) {
        matchIdToUse = "all";
        setSelectedMatch("all");
      }

      //  Fetch markets for this operator
      const marketRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/market-metric?operator_id=${operatorId}&draw_date=${selectedDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      const marketJson = await marketRes.json();
      const marketList = marketJson?.data || [];
      setMarkets([{ id: "all", name: "All Markets" }, ...marketList]);

      if (!marketList.find((m) => String(m.id) === String(selectedMarket))) {
        marketIdToUse = "all";
        setSelectedMarket("all");
      }

      // Fetch users for this operator
      const res = await fetch(`${process.env.REACT_APP_API_HOST}/user-metric`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        body: JSON.stringify({
          draw_date: selectedDate,
          operator_id: operatorId,
        }),
      });
      const data = await res.json();
      const usersFromApi = data?.data || [];

      if (usersFromApi.length === 0) {
        setUsers([{ user_id: "no-user", name: "No Users" }]);
        setSelectedUser("all");
      } else {
        setUsers(usersFromApi);

        // keep user same IF still available under this operator
        if (usersFromApi.some((u) => u.user_id === selectedUser)) {
          // keep same
          setSelectedUser(selectedUser);
        } else {
          // reset only if not present
          setSelectedUser("all");
        }
      }

      // Close dropdown whenever operator changes
      setOpenDropdown(false);
      // keep this if you are handling multi-select separately

      //  Fetch status
      const statusRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/status-metric`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          body: JSON.stringify({
            draw_date: selectedDate,
            operator_id: operatorId,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            user_id: selectedUser || "all",
          }),
        }
      );
      const statusJson = await statusRes.json();
      const statusData = statusJson?.data || [];
      const uniqueStatuses = Array.from(
        new Set(
          statusData.flatMap((item) =>
            Array.isArray(item.status?.data) ? item.status.data : []
          )
        )
      );
      setStatusList(uniqueStatuses);
      setSelectedStatus(uniqueStatuses.length > 0 ? "all" : "no-status");

      // 5️⃣ Fetch all metrics & graphs
      await fetchMetrics({
        marketId: marketIdToUse,
        matchId: matchIdToUse,
        operatorId,
        userId: selectedUser || "all",
        status: selectedStatus || "all",
      });
    } catch (err) {
      console.error("Error fetching data for operator:", err);
      resetMetrics();
    }
  };

  const fetchGraph = async ({ url, method = "POST", body = {} }) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const options =
        method === "GET"
          ? { method, headers }
          : { method, headers, body: JSON.stringify(body) };

      const response = await fetch(url, options);
      if (!response.ok) {
        console.error(`Failed to fetch: ${url}`, response.statusText);
        return null;
      }

      return await response.json();
    } catch (err) {
      console.error(`Error fetching: ${url}`, err);
      return null;
    }
  };
  const resetMetrics = () => {
    setUniqueUsers(0);
    setBetCount(0);
    setTurnOver(0);
    setWonAmount(0);
    setGgrData(null);
    setBetCountGraph([]);
    setTurnOverGraph([]);
    setActiveUser({});
    setTurnOverOperator([]);
    setUniqueUserOperator([]);
    setMatchDetails([]);
    setUsersDetails([]);
  };

  const handleStatusChange = async (statusKey) => {
    const status = statusKey; // always string
    setSelectedStatus(status);

    const marketIdToUse = selectedMarket;
    const matchIdToUse = selectedMatch;
    const operatorIdToUse = selectedOperator;
    const userIdToUse = selectedUser;

    try {
      const statusRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/status-metric`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          body: JSON.stringify({
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            user_id: userIdToUse,
            draw_date: selectedDate,
          }),
        }
      );

      const statusJson = await statusRes.json();
      const statusData = statusJson?.data || [];

      const uniqueStatuses = Array.from(
        new Set(
          statusData.flatMap((item) =>
            Array.isArray(item.status?.data) ? item.status.data : []
          )
        )
      );

      setStatusList(uniqueStatuses);

      if (status !== "all" && !uniqueStatuses.includes(Number(status))) {
        setSelectedStatus("all");
      }

      //  metrics with new status
      await fetchMetrics({
        marketId: marketIdToUse,
        matchId: matchIdToUse,
        operatorId: operatorIdToUse,
        userId: userIdToUse,
        status: status === "all" ? "all" : status,
      });
    } catch (err) {
      console.error("Status change error:", err);
      resetMetrics();
    }
  };

  const fetchMetrics = async ({ matchId, marketId, operatorId, status }) => {
    const matchIdToUse = matchId || "all";
    const marketIdToUse = marketId || "all";
    const operatorIdToUse = operatorId || "all";
    const statusValue = status === "all" ? "all" : Number(status);

    try {
      const [
        uniqueUserBetRes,
        turnoverRes,
        wonJson,
        ggrRes,
        betGraphRes,
        turnoverGraphRes,
        activeUsersRes,
        turnoverOperatorRes,
        uniqueUsersRes,
        matchDetailsRes,
        userDetailsRes,
      ] = await Promise.all([
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-users-betcount`,
          body: {
            match_id: matchIdToUse,
            market_id: marketIdToUse,
            operator_id: operatorIdToUse,
            draw_date: selectedDate,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-count`,
          body: {
            match_id: matchIdToUse,
            market_id: marketIdToUse,
            operator_id: operatorIdToUse,
            draw_date: selectedDate,
            status: statusValue,
          },
        }),
        fetch(`${process.env.REACT_APP_API_HOST}/wonamount`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          body: JSON.stringify({
            draw_date: selectedDate,
            match_id: matchIdToUse,
            market_id: marketIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          }),
        }).then((r) => r.json()),
        fetchGGR({
          draw_date: selectedDate,
          market_id: marketIdToUse,
          match_id: matchIdToUse,
          operator_id: operatorIdToUse,
          status: statusValue,
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-bet-graph`,
          body: {
            bet_date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`,
          body: {
            date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-activeusers-graph`,
          body: {
            draw_date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`,
          body: {
            date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          body: {
            date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-matchDetails`,
          body: {
            draw_date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-userDetails`,
          body: {
            draw_date: selectedDate,
            market_id: marketIdToUse,
            match_id: matchIdToUse,
            operator_id: operatorIdToUse,
            status: statusValue,
          },
        }),
      ]);

      // Set state
      setUniqueUsers(uniqueUserBetRes?.data?.unique_users || 0);
      setBetCount(uniqueUserBetRes?.data?.bet_count || 0);
      setTurnOver(turnoverRes?.data?.turn_over || 0);
      setWonAmount(wonJson?.data || 0);
      setGgrData(ggrRes);
      console.log(turnoverGraphRes, "turnoverGraphRes");

      setBetCountGraph(betGraphRes || []);
      setTurnOverGraph(turnoverGraphRes || []);
      setActiveUser(activeUsersRes || {});
      setTurnOverOperator(turnoverOperatorRes || []);
      setUniqueUserOperator(uniqueUsersRes || []);
      setMatchDetails(matchDetailsRes?.data || []);
      setUsersDetails(userDetailsRes?.data || []);
    } catch (err) {
      console.error("Error fetching metrics:", err);
      resetMetrics();
    }
  };

  // User change
  const handleUserChange = async (e) => {
    const userId = e.target.value; // now could be string or array
    if (!userId || (Array.isArray(userId) && userId.length === 0)) return;

    setSelectedUser(userId); // store array or string

    // Reset metrics
    setTurnOver(0);
    setUniqueUsers(0);
    setBetCount(0);
    setWonAmount(0);
    setGGRAmount(0);
    setGGRPer(0);

    // Prepare API user_id for POST
    const userIdsForAPI =
      Array.isArray(userId) && userId.length > 0 ? userId : [userId];

    // Fetch main metrics
    await refreshAllMetrics({
      date: selectedDate,
      matchId: selectedMatch,
      marketId: selectedMarket,
      operatorId: selectedOperator,
      userId: userIdsForAPI,
    });
  };

  const refreshAllMetrics = async ({
    date,
    matchId,
    marketId,
    operatorId,
    userId,
  }) => {
    try {
      // Normalize user_id for all API requests
      const normalizedUserId =
        Array.isArray(userId) && userId.length > 0
          ? userId.includes("all")
            ? "all"
            : userId
          : "all";

      // Build common request body
      const body = {
        draw_date: date,
        match_id: matchId || "all",
        market_id: marketId || "all",
        operator_id: operatorId || "all",
        user_id: Array.isArray(normalizedUserId)
          ? normalizedUserId
          : [normalizedUserId],
      };

      // Fetch main metrics in parallel
      const [uniqueUserBetRes, turnoverRes, wonAmountRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_HOST}/metric-unique-users-betcount`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify(body),
          }
        ).then((r) => r.json()),

        fetch(`${process.env.REACT_APP_API_HOST}/metric-turnover-count`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify(body),
        }).then((r) => r.json()),

        fetch(`${process.env.REACT_APP_API_HOST}/wonamount`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify(body),
        }).then((r) => r.json()),
      ]);

      //  Fetch GGR amount (fixed undefined variable)
      const ggrRes = await fetchGGR({
        draw_date: date,
        match_id: matchId || "all",
        operator_id: operatorId || "all",
        user_id: normalizedUserId,
      });
      setGgrData(ggrRes);

      //  Update main metric states
      setUniqueUsers(uniqueUserBetRes?.data?.unique_users || 0);
      setBetCount(uniqueUserBetRes?.data?.bet_count || 0);
      setTurnOver(turnoverRes?.data?.turn_over || 0);
      setWonAmount(wonAmountRes?.data || 0);

      //  Fetch graphs in parallel using same normalized user filter
      const [
        betGraphRes,
        turnoverGraphRes,
        activeUsersRes,
        turnoverOperatorRes,
        uniqueOperatorRes,
      ] = await Promise.all([
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-bet-graph`,
          body: {
            bet_date: date,
            match_id: matchId || "all",
            operator_id: operatorId || "all",
            user_id: normalizedUserId,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`,
          body: {
            date,
            match_id: matchId || "all",
            operator_id: operatorId || "all",
            user_id: normalizedUserId,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-activeusers-graph?draw_date=${date}`,
          body: {
            draw_date: date,
            match_id: matchId || "all",
            operator_id: operatorId || "all",
            user_id: normalizedUserId,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`,
          body: {
            date,
            operator_id: operatorId || "all",
            user_id: normalizedUserId,
          },
        }),
        fetchGraph({
          url: `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          body: {
            date,
            operator_id: operatorId || "all",
            user_id: normalizedUserId,
          },
        }),
      ]);

      //  Update all graph states
      setBetCountGraph(betGraphRes || []);
      setTurnOverGraph(turnoverGraphRes || []);
      setActiveUser(activeUsersRes || {});
      setTurnOverOperator(turnoverOperatorRes || []);
      setUniqueUserOperator(uniqueOperatorRes || []);
    } catch (err) {
      console.error("Error refreshing metrics:", err);
      setUniqueUsers(0);
      setBetCount(0);
      setTurnOver(0);
    }
  };

  //fetch user metrics

  // Amount formatters
  const formatAmount = (num) => {
    if (num == null) return "0";
    return new Intl.NumberFormat("en-IN").format(Number(num));
  };
  // Percent formatter
  const formatPercent = (num) => {
    if (num == null) return "0";
    return `${Math.round(num)}%`; // round & add %
  };

  // Sorting functions
  const handleSort = (tableType, key) => {
    const currentSort = sortConfig[tableType];
    let direction = "asc";

    if (currentSort.key === key && currentSort.direction === "asc") {
      direction = "desc";
    }

    setSortConfig((prev) => ({
      ...prev,
      [tableType]: { key, direction },
    }));
  };

  const sortData = (data, tableType) => {
    const currentSort = sortConfig[tableType];
    if (!currentSort.key) return data;

    return [...data].sort((a, b) => {
      let aVal, bVal;

      switch (currentSort.key) {
        case "turnover":
          aVal = parseFloat(a.turnover) || 0;
          bVal = parseFloat(b.turnover) || 0;
          break;
        case "margin":
          aVal = parseFloat(a.margin) || 0;
          bVal = parseFloat(b.margin) || 0;
          break;
        default:
          return 0;
      }

      if (currentSort.direction === "asc") {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
  };

  const getSortIcon = (tableType, key) => {
    const currentSort = sortConfig[tableType];
    if (currentSort.key !== key) return "⇅";
    return currentSort.direction === "asc" ? "↑" : "↓";
  };
  // Stats array
  const stats = [
    { label: "Unique Users", value: uniqueUsers ?? 0 },
    { label: "Bet Count", value: betCount ?? 0 },
    { label: "Turnover", value: formatAmount(turnOver) },
    { label: "Won Amount", value: formatAmount(wonAmount) },
    {
      label: "GGR Amount",
      value: formatAmount(GGRAmount),
      color:
        GGRAmount > 0
          ? "text-green-600"
          : GGRAmount < 0
          ? "text-red-600"
          : "text-gray-700",
    },
    { label: "GGR %", value: formatPercent(GGRPer) },
  ];

  useEffect(() => {
    setGGRAmount(turnOver - wonAmount);
  }, [turnOver, wonAmount]);
  useEffect(() => {
    if (turnOver > 0) {
      setGGRPer(((turnOver - wonAmount) * 100) / turnOver);
    }
  }, [turnOver, wonAmount]);

  // Fetch matches when selectedDate changes
  useEffect(() => {
    const getMatchesMetric = async () => {
      try {
        const headers = new Headers({
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/match-metric?draw_date=${selectedDate}`,
          { method: "GET", headers }
        );

        const result = await response.json();

        if (!result.data || result.data.length === 0) {
          setMatches([{ id: "no-match", name: "No Matches" }]);
          setSelectedMatch("no-match");
          return;
        }

        const matchData = [
          { id: "all", name: "All Matches" },
          ...result.data.map((m) => ({ ...m, id: String(m.id) })),
        ];

        setMatches(matchData);
        setSelectedMatch("all"); // default
      } catch (err) {
        console.error("Error fetching matches:", err);
        setMatches([{ id: "no-match", name: "No Matches" }]);
        setSelectedMatch("no-match");
      }
    };

    getMatchesMetric();
  }, []); //  empty dependency array → runs only once

  // Unique Users and Bet Count
  useEffect(() => {
    const getUsersCountBetCount = async () => {
      try {
        const body = {
          draw_date: selectedDate,
          user_id: ["all"], //  Pass all users
          match_id: "all", // optional filter
          market_id: "all", // optional filter
          operator_id: "all", // optional filter
        };

        const res = await fetch(
          process.env.REACT_APP_API_HOST + "/metric-unique-users-betcount",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify(body),
          }
        );

        if (res.status === 401) {
          console.log("Unauthorized access. Please log in again.");
          return;
        }

        const result = await res.json();
        if (result.success && result.data) {
          setUniqueUsers(result.data.unique_users);
          setBetCount(result.data.bet_count);
        } else {
          setUniqueUsers(0);
          setBetCount(0);
        }
      } catch (err) {
        console.error("Error fetching unique users and bet count:", err);
        setUniqueUsers(0);
        setBetCount(0);
      }
    };

    getUsersCountBetCount();
  }, []);

  // Turnover
  useEffect(() => {
    const getTurnoverMetric = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/metric-turnover-count`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify({
              draw_date: selectedDate,
              user_id: ["all"], // send as array
              match_id: "all",
              market_id: "all",
              operator_id: "all",
              status: "all", // optional
            }),
          }
        );

        if (response.status === 401) {
          console.log("Unauthorized access. Please log in again.");
          return;
        }

        const result = await response.json();

        if (result.data) {
          setTurnOver(result.data.turn_over || 0);
        }
      } catch (err) {
        console.log("Error fetching turnover:", err);
      }
    };

    getTurnoverMetric();
  }, []);

  // Won Amount
  const fetchWonAmount = async (selectedDate) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
          match_id: "all",
          draw_date: selectedDate,
        }),
      };

      const url = `${process.env.REACT_APP_API_HOST}/wonamount`;

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        console.error("Failed to fetch won amount:", response.statusText);
        return;
      }

      const result = await response.json();

      if (result?.success && result?.data) {
        const wonData = result?.data;
        setWonAmount(wonData);
      }
    } catch (err) {
      console.error("Error fetching won amount:", err);
    }
  };
  useEffect(() => {
    fetchWonAmount(selectedDate);
  }, []);

  // GGR Data

  useEffect(() => {
    const fetchGGRByDate = async () => {
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append(
          "Authorization",
          "Bearer " + localStorage.getItem("jwt")
        );

        const requestOptions = {
          method: "POST",
          headers,
          body: JSON.stringify({
            draw_date: selectedDate,
            match_id: selectedMatch,
          }),
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/grr-amount`,
          requestOptions
        );

        if (response.status === 401) {
          console.log("Unauthorized access. Please log in again.");
          return;
        }

        const result = await response.json();
        if (result.success) {
          setGgrData(result.data);
        } else {
          console.log("No GGR data found:", result.message);
          setGgrData({}); // reset if no data
        }
      } catch (err) {
        console.log("Error fetching GGR data:", err);
        setGgrData({}); // reset on error
      }
    };

    if (selectedDate) {
      fetchGGRByDate();
    }
  }, [selectedDate]); // runs whenever date or match changes

  // Unique Users by Operator
  useEffect(() => {
    const uniqueUsersByOperator = async (date) => {
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append(
          "Authorization",
          "Bearer " + localStorage.getItem("jwt")
        );

        const requestOptions = {
          method: "POST",
          headers,
          body: JSON.stringify({ date }),
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/metric-unique-operator-graph`,
          requestOptions
        );

        if (response.status === 401) {
          console.log("Unauthorized access. Please log in again.");
          return;
        }

        const result = await response.json();
        if (result?.data) {
          setUniqueUserOperator(result.data);
        }
      } catch (err) {
        console.log("Error fetching events:", err);
      }
    };

    if (selectedDate) {
      uniqueUsersByOperator(selectedDate);
    }
  }, [selectedDate]); // now runs whenever selectedDate changes

  // Turnover by Game
  const fetchTurnOver = async (selectedDate) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({ date: selectedDate }),
      };

      const url = `${process.env.REACT_APP_API_HOST}/metric-turnover-game-graph`;

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        console.error("Failed to fetch turnover:", response.statusText);
        return;
      }

      const result = await response.json();
      if (result && result.length > 0) {
        const turnOverData = result.map((item) => ({
          name: item.name,
          turnOver: item.value,
        }));

        setTurnOverGraph(turnOverData);
      } else {
        setTurnOverGraph([]);
      }
    } catch (err) {
      console.error("Error fetching turnover:", err);
    }
  };
  useEffect(() => {
    fetchTurnOver(selectedDate);
  }, []);
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_HOST}/status-metric`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
            body: JSON.stringify({
              draw_date: selectedDate,
              market_id: selectedMarket || "all",
              match_id: selectedMatch || "all",
              operator_id: selectedOperator || "all",
              user_id: selectedUser || "all",
            }),
          }
        );

        const json = await res.json();

        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          const uniqueStatuses = [
            ...new Set(
              json.data
                .map((item) => item.status?.data?.[0] ?? null)
                .filter((v) => v !== null)
            ),
          ];

          setStatusList(uniqueStatuses);

          const validKeys = uniqueStatuses.map(String);

          // Initial load → set default
          if (!selectedStatus) {
            setSelectedStatus("all");
            return;
          }

          // If current selection invalid → reset to "all"
          if (
            selectedStatus !== "all" &&
            selectedStatus !== "no-status" &&
            !validKeys.includes(selectedStatus)
          ) {
            setSelectedStatus("all");
          }
        } else {
          setStatusList([]);
          setSelectedStatus("no-status");
        }
      } catch (e) {
        console.error(e);
        setStatusList([]);
        setSelectedStatus("no-status");
      }
    };

    fetchStatus();
  }, [,]);

  // Turnover by Operator
  const fetchTurnOverOperator = async (selectedDate) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({ date: selectedDate }),
      };

      const url = `${process.env.REACT_APP_API_HOST}/metrics-turnoveroperators-graph`;

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        console.error(
          "Failed to fetch turnover operator:",
          response.statusText
        );
        return;
      }

      const result = await response.json();
      if (result) {
        setTurnOverOperator(result);
      } else {
        setTurnOverOperator([]);
      }
    } catch (err) {
      console.error("Error fetching turnover operator:", err);
    }
  };

  useEffect(() => {
    fetchTurnOverOperator(selectedDate);
  }, []);

  // Bet Count by Game
  const fetchBets = async () => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
          bet_date: selectedDate,
        }),
      };

      const url = `${process.env.REACT_APP_API_HOST}/metric-bet-graph`;

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        console.error("Failed to fetch turnover:", response.statusText);
        return;
      }

      const result = await response.json();
      if (result && result.length > 0) {
        const betData = result.map((item) => ({
          bets: item.bets,
          id: Number(item.id),
          name: item.name,
        }));

        setBetCountGraph(betData);
      } else {
        setBetCountGraph([]);
      }
    } catch (err) {
      console.error("Error fetching turnover:", err);
    }
  };
  useEffect(() => {
    fetchBets();
  }, []);

  // Fetch Markets on component mount
  useEffect(() => {
    const getMarkets = async () => {
      try {
        const headers = new Headers({
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        });

        const drawDate = selectedDate || new Date().toISOString().split("T")[0];

        const res = await fetch(
          `${process.env.REACT_APP_API_HOST}/market-metric?draw_date=${drawDate}`,
          { method: "GET", headers }
        );

        const result = await res.json();

        if (!result.data || result.data.length === 0) {
          setMarkets([{ id: "no-market", name: "No Markets" }]);
          setSelectedMarket("no-market");
        } else {
          setMarkets([{ id: "all", name: "All Markets" }, ...result.data]);
          setSelectedMarket("all");
        }
      } catch (err) {
        console.error("Error fetching markets:", err);
        setMarkets([{ id: "no-market", name: "No Markets" }]);
        setSelectedMarket("no-market");
      }
    };

    getMarkets();
  }, []);

  // Fetch Operators when selectedDate, selectedMatch, or selectedMarket changes
  useEffect(() => {
    const fetchOperators = async () => {
      if (!selectedDate || !selectedMarket) {
        setOperators([{ id: "no-operator", name: "No Operators" }]);
        setSelectedOperator("no-operator");
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_HOST}/operator-metric?draw_date=${selectedDate}&market_id=${selectedMarket}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
          }
        );

        const result = await res.json();

        let operatorList = [];

        if (
          result.success &&
          Array.isArray(result.data) &&
          result.data.length > 0
        ) {
          operatorList = [
            { id: "all", name: "All Operators" },
            ...result.data.map((op) => ({ id: op.id, name: op.operator_id })),
          ];
        } else {
          operatorList = [{ id: "no-operator", name: "No Operators" }];
        }

        setOperators(operatorList);
        setSelectedOperator(operatorList[0].id); // "all" or "no-operator"
      } catch (err) {
        console.error("Error fetching operators:", err);
        setOperators([{ id: "no-operator", name: "No Operators" }]);
        setSelectedOperator("no-operator");
      }
    };

    fetchOperators();
  }, []); 

  // Fetch Users when selectedDate, selectedMatch, selectedMarket, or selectedOperator changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const body = {
          draw_date: selectedDate || new Date().toISOString().split("T")[0],
          match_id: "all",
          market_id: "all",
          operator_id: "all",
          user_id: ["all"],
        };

        const url = `${process.env.REACT_APP_API_HOST}/user-metric`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify(body),
        });

        const result = await res.json();
        const userList = result.data || [];

        // No users → show "No Users"
        if (result.success === false || userList.length === 0) {
          setUsers([]);
          setSelectedUser("no-user");
          setUserSearchValue("");
          return;
        }

        // API has users → show "All Users"
        const allOption = { user_id: "all" };
        setUsers([allOption, ...userList]);
        setSelectedUser("all");
      } catch (err) {
        console.error("Error fetching users:", err);
        setUsers([]);
        setSelectedUser("no-user");
        setUserSearchValue("");
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search value
  useEffect(() => {
    // Only filter if we have users
    if (!users || users.length === 0) {
      setFilteredUsers([]);
      return;
    }

    // If search is empty or "All Users" text, show all users
    if (
      !userSearchValue ||
      userSearchValue.trim() === "" ||
      userSearchValue.toLowerCase() === "all users"
    ) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) => {
        // Always include "All Users" option in search results
        if (user.user_id === "all") {
          return true;
        }
        const userName = (user.user || user.user_name || "")
          .toString()
          .toLowerCase();
        const userId = String(user.user_id || "").toLowerCase();
        const searchLower = userSearchValue.toLowerCase().trim();
        return userName.includes(searchLower) || userId.includes(searchLower);
      });
      setFilteredUsers(filtered);
    }
  }, [userSearchValue, users]);

  // Filter markets based on search value
  useEffect(() => {
    if (!Array.isArray(markets) || markets.length === 0) {
      setFilteredMarkets([]);
      return;
    }

    const searchLower = marketSearchValue?.toLowerCase().trim();

    const selectedLabelLower = (() => {
      if (!selectedMarket) return "";
      if (selectedMarket === "all") return "all markets";
      if (selectedMarket === "no-market") return "no market";

      const selectedMarketObj = markets.find(
        (market) => String(market.id) === String(selectedMarket)
      );
      return (
        selectedMarketObj?.name?.toString().toLowerCase() ||
        String(selectedMarketObj?.id || "").toLowerCase()
      );
    })();

    if (
      !searchLower ||
      searchLower === "" ||
      searchLower === "all markets" ||
      searchLower === selectedLabelLower
    ) {
      setFilteredMarkets(markets);
      return;
    }

    const filtered = markets.filter((market) => {
      if (market.id === "all") return true;
      const marketName = (market.name || "").toString().toLowerCase();
      const marketId = String(market.id || "").toLowerCase();
      return marketName.includes(searchLower) || marketId.includes(searchLower);
    });

    setFilteredMarkets(filtered);
  }, [marketSearchValue, markets, selectedMarket]);

  // Keep market search input in sync with selection/state changes
  useEffect(() => {
    if (
      Array.isArray(markets) &&
      markets.length === 1 &&
      markets[0]?.id === "no-market"
    ) {
      setMarketSearchValue("No Market");
      return;
    }

    if (!selectedMarket) {
      if (!markets || markets.length === 0) {
        setMarketSearchValue("");
      }
      return;
    }

    if (!Array.isArray(markets) || markets.length === 0) {
      if (selectedMarket === "all") {
        setMarketSearchValue("All Markets");
      } else if (selectedMarket === "no-market") {
        setMarketSearchValue("No Market");
      }
      return;
    }

    const selectedMarketObj = markets.find(
      (market) => String(market.id) === String(selectedMarket)
    );

    if (!selectedMarketObj) {
      if (selectedMarket === "all") {
        setMarketSearchValue("All Markets");
      } else if (selectedMarket === "no-market") {
        setMarketSearchValue("No Market");
      }
      return;
    }

    if (selectedMarketObj.id === "all") {
      setMarketSearchValue("All Markets");
    } else if (selectedMarketObj.id === "no-market") {
      setMarketSearchValue("No Market");
    } else {
      setMarketSearchValue(selectedMarketObj.name?.toString() || "");
    }
  }, [selectedMarket, markets]);

  // Filter matches based on search value
  useEffect(() => {
    if (!Array.isArray(matches) || matches.length === 0) {
      setFilteredMatches([]);
      return;
    }

    const searchValue = matchSearchValue?.toLowerCase().trim();
    if (
      !matchSearchValue ||
      searchValue === "" ||
      searchValue === "all matches"
    ) {
      setFilteredMatches(matches);
      return;
    }

    const filtered = matches.filter((match) => {
      if (match.id === "all") return true;
      const matchName = (match.name || "").toString().toLowerCase();
      const matchId = String(match.id || "").toLowerCase();
      return matchName.includes(searchValue) || matchId.includes(searchValue);
    });

    setFilteredMatches(filtered);
  }, [matchSearchValue, matches]);

  // Keep match search display in sync with selected match
  useEffect(() => {
    if (isMatchDropdownOpen) return;

    const displayLabel = getSelectedMatchLabel();
    if (matchSearchValue === displayLabel) return;

    setMatchSearchValue(displayLabel);
  }, [getSelectedMatchLabel, isMatchDropdownOpen, matchSearchValue]);

  // Fetch User Details when selectedDate changes
  const fetchUserDetails = async () => {
    if (!selectedDate) return;

    try {
      const body = {
        draw_date: selectedDate,
      };

      if (
        selectedUser &&
        selectedUser !== "all" &&
        selectedUser !== "no-user"
      ) {
        body.user_id = String(selectedUser);
      }

      if (
        selectedMarket &&
        selectedMarket !== "all" &&
        selectedMarket !== "no-market"
      ) {
        body.market_id = String(selectedMarket);
      }

      if (
        selectedMatch &&
        selectedMatch !== "all" &&
        selectedMatch !== "no-match"
      ) {
        body.match_id = String(selectedMatch);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/metrics-userDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error("Failed to fetch user details");

      const result = await res.json();
      setUsersDetails(result.data || []);
    } catch (err) {
      console.error("Error fetching user details:", err);
      setUsers([]);
      setSelectedUser(null);
    }
  };
  useEffect(() => {
    fetchUserDetails();
    fetchMatchDetails();
  }, [selectedDate]);

  // Turnover Matches

  const fetchMatchDetails = async () => {
    if (!selectedDate) return;

    try {
      const body = {
        draw_date: selectedDate,
      };

      // Market filter
      if (
        selectedMarket &&
        selectedMarket !== "all" &&
        selectedMarket !== "no-market"
      ) {
        body.market_id = String(selectedMarket);
      }

      // Match filter
      if (
        selectedMatch &&
        selectedMatch !== "all" &&
        selectedMatch !== "no-match"
      ) {
        body.match_id = String(selectedMatch);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/metrics-matchDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error("Failed to fetch match details");

      const result = await res.json();
      setMatchDetails(result.data || []);
    } catch (err) {
      console.error("Error fetching match details:", err);
      setMatches([]);
      setSelectedMatch(null);
    }
  };
  useEffect(() => {
    fetchMatchDetails();
  }, []);

  useEffect(() => {
    // Show spinner for 1 second on mount
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  const marketSelectDisabled = !Array.isArray(markets) || markets.length === 0;
  const matchSelectDisabled = !Array.isArray(matches) || matches.length === 0;

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner size="lg" variant="spinner" color="warning" />
      </div>
    );
  }

  return (
    <div className="min-h-[400px]" id="dashboard-container">
      <div className="flex flex-col gap-2">
        {/* <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md overflow-hidden border border-gray-200">
            <div className="text-m_blue px-3 py-3 flex items-center justify-center sm:text-sm xl:text-md text-xs">
              <CalendarIcon />
            </div>
            <select
              className="px-2 py-2 outline-none text-sm"
              value={selectedRange}
              onChange={(e) => setSelectedRange(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7">Last 7 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="last30">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {selectedRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="px-2 py-2 border border-gray-200 rounded"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                className="px-2 py-2 border border-gray-200 rounded"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </div>
          )}
        </div> */}
        {/* Filters Section */}
        <div className="grid w-full gap-2 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
          {/* Date Picker */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between">
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Date
            </p>
            <input
              type="date"
              name="draw_date"
              id="draw_date"
              className="w-full border border-gray-300 rounded-md p-2 bg-white/70 focus:outline-none focus:ring focus:ring-blue-200 focus:border-blue-500"
              value={selectedDate}
              max={today}
              onChange={handleDateChange}
            />
          </div>

          {/* Market */}
          {/* Market Dropdown */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between">
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Market
            </p>

            <Autocomplete
              size="sm"
              placeholder={
                marketSelectDisabled ? "No Markets" : "Search and select Market"
              }
              selectedKey={selectedMarket ? String(selectedMarket) : null}
              inputValue={marketSearchValue}
              onInputChange={(value) => setMarketSearchValue(value)}
              onOpenChange={(isOpen) => {
                if (
                  isOpen &&
                  selectedMarket === "all" &&
                  marketSearchValue === "All Markets"
                ) {
                  setMarketSearchValue("");
                }
                if (
                  !isOpen &&
                  selectedMarket === "all" &&
                  (!marketSearchValue || marketSearchValue.trim() === "")
                ) {
                  setMarketSearchValue("All Markets");
                }
              }}
              onSelectionChange={(key) => {
                if (key === null || key === undefined) return;
                const marketId = String(key);
                if (marketId === "no-market") return;

                const selectedMarketObj = markets.find(
                  (market) => String(market.id) === marketId
                );
                if (selectedMarketObj) {
                  if (selectedMarketObj.id === "all") {
                    setMarketSearchValue("All Markets");
                  } else {
                    setMarketSearchValue(
                      selectedMarketObj.name?.toString() || ""
                    );
                  }
                }

                handleMarketChange(marketId);
              }}
              isDisabled={marketSelectDisabled}
              allowsCustomValue={false}
              items={filteredMarkets}
              emptyContent={
                marketSelectDisabled
                  ? "No markets available"
                  : "No markets found"
              }
            >
              {(market) => (
                <AutocompleteItem
                  key={String(market.id)}
                  value={String(market.id)}
                  isDisabled={market.id === "no-market"}
                >
                  {market.id === "all" ? "All Markets" : market.name}
                </AutocompleteItem>
              )}
            </Autocomplete>
          </div>

          {/* Match Dropdown */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between">
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Match
            </p>

            <Autocomplete
              key={selectedDate + selectedMarket}
              size="sm"
              placeholder={
                matchSelectDisabled ? "No Matches" : "Search and select Match"
              }
              selectedKey={selectedMatch ? String(selectedMatch) : null}
              inputValue={matchSearchValue}
              onInputChange={(value) => setMatchSearchValue(value)}
              onOpenChange={(isOpen) => {
                setIsMatchDropdownOpen(isOpen);
                const displayLabel = getSelectedMatchLabel() || "";

                if (isOpen) {
                  const currentValue =
                    matchSearchValue?.trim().toLowerCase() || "";
                  if (
                    currentValue === "" ||
                    currentValue === displayLabel.trim().toLowerCase()
                  ) {
                    setMatchSearchValue("");
                  }
                  return;
                }

                if (!matchSearchValue || matchSearchValue.trim() === "") {
                  setMatchSearchValue(displayLabel);
                }
              }}
              onSelectionChange={(key) => {
                if (key === null || key === undefined) return;
                const matchId = String(key);
                if (matchId === "no-match") return;

                const selectedMatchObj = matches.find(
                  (match) => String(match.id) === matchId
                );
                if (selectedMatchObj) {
                  if (selectedMatchObj.id === "all") {
                    setMatchSearchValue("All Matches");
                  } else {
                    setMatchSearchValue(
                      selectedMatchObj.name?.toString() || ""
                    );
                  }
                }

                handleMatchChange(matchId);
              }}
              isDisabled={matchSelectDisabled}
              allowsCustomValue={false}
              items={filteredMatches}
              emptyContent={
                matchSelectDisabled
                  ? "No matches available"
                  : "No matches found"
              }
            >
              {(match) => (
                <AutocompleteItem
                  key={String(match.id)}
                  value={String(match.id)}
                  isDisabled={match.id === "no-match"}
                >
                  {match.id === "all" ? "All Matches" : match.name}
                </AutocompleteItem>
              )}
            </Autocomplete>
          </div>

          {/* Operator Dropdown */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between">
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Operator
            </p>

            <Select
              size="sm"
              selectedKeys={new Set([selectedOperator || "all"])}
              onChange={handleOperatorChange}
              isDisabled={!Array.isArray(operators) || operators.length === 0}
            >
              {Array.isArray(operators) && operators.length > 0 ? (
                operators.map((op) => (
                  <SelectItem key={op.id} value={op.id}>
                    {op.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem key="no-operator" value="no-operator" disabled>
                  No Operators
                </SelectItem>
              )}
            </Select>
          </div>

          {/* Users */}
          <div
            className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between relative"
            ref={dropdownRef}
          >
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Users
            </p>

            <button
              onClick={() =>
                isUserDropdownEnabled && setOpenDropdown((prev) => !prev)
              }
              disabled={!isUserDropdownEnabled}
              className={`w-full border rounded-md px-3 py-2 text-left text-sm flex justify-between items-center 
      ${
        !isUserDropdownEnabled
          ? "bg-gray-100 cursor-not-allowed text-gray-400"
          : ""
      }`}
            >
              <span>
                {!isUserDropdownEnabled
                  ? "All Users"
                  : users.length === 0 || users[0].user_id === "no-user"
                  ? "No Users"
                  : selectedUsers.length === 0
                  ? "All Users"
                  : selectedUsers.length === users.length - 1
                  ? "All Users"
                  : `${selectedUsers.length} Selected`}
              </span>

              <svg
                className={`w-5 h-5 text-gray-600 transition-transform ${
                  openDropdown ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {openDropdown && isUserDropdownEnabled && (
              <div className="absolute z-10 top-full left-0 mt-1 w-full bg-white shadow rounded-xl border border-gray-200 p-3">
                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchValue}
                  onChange={(e) => setUserSearchValue(e.target.value)}
                  className="w-full mb-2 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />

                <div className="flex justify-between mb-2">
                  <button
                    onClick={handleSelectAll}
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      selectedUsers.length === filteredUserList.length
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      selectedUsers.length === 0
                        ? "bg-gray-200 text-gray-700"
                        : "bg-blue-500 text-white"
                    }`}
                  >
                    Deselect All
                  </button>
                </div>

                <div className="max-h-40 overflow-y-auto">
                  {filteredUserList.length > 0 ? (
                    filteredUserList.map((user) => (
                      <label
                        key={user.user_id}
                        className="flex items-center gap-2 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => toggleUser(user.user_id)}
                          className="accent-blue-500"
                        />
                        {user.name || user.user_id}
                      </label>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm text-center mt-2">
                      No users found
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-100 p-3 flex flex-col justify-between">
            <p className="text-sm font-semibold text-m_black uppercase mb-1">
              Status
            </p>

            <Autocomplete
              size="sm"
              selectedKey={
                statusList.length === 0
                  ? "no-status"
                  : !selectedStatus || selectedStatus === "all"
                  ? "all"
                  : String(selectedStatus)
              }
              onSelectionChange={handleStatusChange}
            >
              {statusList.length === 0 ? (
                <SelectItem key="no-status" value="no-status">
                  No Status
                </SelectItem>
              ) : (
                <>
                  <SelectItem key="all" value="all">
                    All Status
                  </SelectItem>

                  {statusList.includes(0) && (
                    <SelectItem key="0" value="0">
                      Open
                    </SelectItem>
                  )}

                  {statusList.includes(1) && (
                    <SelectItem key="1" value="1">
                      Close
                    </SelectItem>
                  )}
                </>
              )}
            </Autocomplete>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid w-full mt-1 gap-2 [grid-template-columns:repeat(auto-fit,minmax(90px,1fr))]">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-lg shadow border border-gray-100 px-2 py-3 text-center min-w-[70px] hover:scale-105 transition-transform duration-150"
            >
              <p className="text-[10px] uppercase font-semibold text-gray-500 truncate">
                {s.label}
              </p>
              <p className={`text-sm font-bold text-gray-800 ${s.color || ""}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 border-t border-gray-200"></div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
        <div className="bg-white  rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <ApexChart
            betCountGraph={betCountGraph}
            key={betCountGraph?.length}
          />
        </div>
        <div className="bg-white  rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <TurnoverBar data={turnOverGraph} key={turnOverGraph?.length} />
        </div>
        <div className="bg-white  rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <GGRLine data={ggrData} key={ggrData?.length} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2 mt-3">
        {/* Tall left section spanning 2 rows */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 md:row-span-2 min-w-0">
          <Tabs
            key="full"
            aria-label="Tabs radius"
            radius="full"
            className="flex justify-center"
            selectedKey={selected}
            onSelectionChange={setSelected}
            classNames={{
              tabList: "bg-black rounded-full p-1 w-full overflow-x-auto",
              tab: "min-w-[120px] sm:min-w-[140px] text-white data-[selected=true]:bg-gray-200",
              cursor: "bg-gray-200",
              tabContent: "text-white",
            }}
            size="lg"
          >
            <Tab
              key="turnover"
              title="Turnover"
              className="min-w-[120px] sm:min-w-[140px] text-white data-[selected=true]:bg-gray-600"
            >
              <div className="flex flex-col h-[calc(100vh-200px)] gap-3 overflow-auto">
                {/*  Inner wrapper with equal-height two sections */}
                <div className="grid grid-rows-2 flex-1 gap-3">
                  {/* 🔹 Section 1 */}
                  <div className="flex justify-center text-black bg-white rounded-lg border overflow-auto">
                    <div className="flex-col text-center w-full">
                      <h2 className="mb-2 sticky top-0 bg-[#1c232f0a] py-2 border-b">
                        GAMES
                      </h2>
                      <div className="w-full overflow-x-auto max-h-full">
                        <Table
                          aria-label="Turnover table"
                          isHeaderSticky
                          className="min-w-[720px] md:min-w-0"
                        >
                          <TableHeader>
                            <TableColumn>Game</TableColumn>
                            <TableColumn>
                              <div
                                className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                                onClick={() =>
                                  handleSort("turnoverGames", "turnover")
                                }
                              >
                                Turnover{" "}
                                {getSortIcon("turnoverGames", "turnover")}
                              </div>
                            </TableColumn>
                            <TableColumn>Bet Counts</TableColumn>
                            <TableColumn>Margins %</TableColumn>
                          </TableHeader>

                          <TableBody>
                            {sortData(matchDetails, "turnoverGames").map(
                              (row, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <button
                                      onClick={() => handleMatchClick(row.id)}
                                      className="truncate px-3 py-1.5 text-xs font-medium rounded-md transition border border-blue-700 bg-blue-700 text-white hover:bg-white hover:text-blue-700 "
                                    >
                                      {row.name}
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {row.turnover}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {row.betcount}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(
                                      (row.margin * 100) /
                                      row.turnover
                                    ).toFixed(2)}
                                    %
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>

                  {/* 🔹 Section 2 */}
                  <div className="flex justify-center text-black bg-white rounded-lg border overflow-auto">
                    <div className="flex-col text-center w-full">
                      <h2 className="mb-2 sticky top-0 bg-[#1c232f0a] py-2 border-b">
                        USERS
                      </h2>
                      <div className="w-full overflow-x-auto max-h-full">
                        <Table
                          aria-label="Turnover table"
                          isHeaderSticky
                          className="min-w-[720px] md:min-w-0"
                        >
                          <TableHeader>
                            <TableColumn>User</TableColumn>
                            <TableColumn>Operator</TableColumn>
                            <TableColumn>
                              <div
                                className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                                onClick={() =>
                                  handleSort("turnoverUsers", "turnover")
                                }
                              >
                                Turnover{" "}
                                {getSortIcon("turnoverUsers", "turnover")}
                              </div>
                            </TableColumn>
                            <TableColumn>Bet Counts</TableColumn>
                            <TableColumn>Margins %</TableColumn>
                          </TableHeader>

                          <TableBody>
                            {loadingUsers ? (
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  className="text-center py-7"
                                >
                                  <Spinner
                                    size="lg"
                                    variant="spinner"
                                    color="warning"
                                  />
                                </TableCell>
                              </TableRow>
                            ) : (
                              sortData(usersDetails, "turnoverUsers").map(
                                (row, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      <button
                                        onClick={() =>
                                          handleUserClick(
                                            row.user_id,
                                            row.operator_id
                                          )
                                        }
                                        className="truncate px-3 py-1.5 text-xs font-medium rounded-md transition border border-blue-800 bg-blue-800 text-white hover:bg-gray-200 hover:text-blue-800"
                                      >
                                        {row.user}
                                      </button>
                                    </TableCell>
                                    <TableCell>{row.operator_name}</TableCell>
                                    <TableCell className="text-right">
                                      {row.turnover}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {row.betcount}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {" "}
                                      {(
                                        (row.margin * 100) /
                                        row.turnover
                                      ).toFixed(2)}
                                      %
                                    </TableCell>
                                  </TableRow>
                                )
                              )
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab
              key="winners"
              title="Winners"
              className="min-w-[120px] sm:min-w-[140px] text-white data-[selected=true]:bg-gray-600"
            >
              <div className="flex justify-center text-black bg-white">
                <div className="flex-col text-center w-full">
                  <h2 className="mb-2">USERS</h2>

                  {/* Scrollable wrapper */}
                  <div className="w-full overflow-x-auto overflow-y-auto border rounded-lg max-h-64 md:max-h-[620px]">
                    <Table
                      aria-label="Winner table"
                      isHeaderSticky
                      className="min-w-[720px] md:min-w-0"
                    >
                      <TableHeader>
                        <TableColumn>User</TableColumn>
                        <TableColumn>
                          <div
                            className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => handleSort("winners", "turnover")}
                          >
                            Turnover {getSortIcon("winners", "turnover")}
                          </div>
                        </TableColumn>
                        <TableColumn>
                          <div
                            className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => handleSort("winners", "margin")}
                          >
                            Margin Amount {getSortIcon("winners", "margin")}
                          </div>
                        </TableColumn>
                        <TableColumn>Bet Counts</TableColumn>
                        <TableColumn>Margins %</TableColumn>
                      </TableHeader>

                      <TableBody>
                        {sortData(usersDetails, "winners").map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <button
                                onClick={() =>
                                  handleUserClick(row.user_id, row.operator_id)
                                }
                                className="truncate px-3 py-1.5 text-xs font-medium rounded-md border border-blue-800 bg-blue-800 text-white hover:bg-gray-200 hover:text-blue-800 transition"
                              >
                                {row.user}
                              </button>
                            </TableCell>
                            <TableCell className="text-right">
                              {row.turnover}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.margin}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.betcount}
                            </TableCell>
                            <TableCell className="text-green-500 text-right">
                              {((row.margin * 100) / row.turnover).toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab
              key="contributors"
              title="Contributors"
              className="min-w-[120px] sm:min-w-[140px] text-white data-[selected=true]:bg-gray-600"
            >
              <div className="flex justify-center text-black bg-white">
                <div className="flex-col text-center w-full">
                  <h2 className="mb-2">USERS</h2>
                  {/* Scrollable wrapper */}
                  <div className="w-full overflow-x-auto overflow-y-auto border rounded-lg max-h-64 md:max-h-[620px]">
                    <Table
                      aria-label="Turnover table"
                      isHeaderSticky
                      className="min-w-[720px] md:min-w-0"
                    >
                      <TableHeader>
                        <TableColumn>User</TableColumn>
                        <TableColumn>Operator</TableColumn>
                        <TableColumn>Turnover</TableColumn>
                        <TableColumn>
                          <div
                            className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => handleSort("contributors", "margin")}
                          >
                            Margin Amount{" "}
                            {getSortIcon("contributors", "margin")}
                          </div>
                        </TableColumn>
                        <TableColumn>Bet Counts</TableColumn>
                        <TableColumn>Margins %</TableColumn>
                      </TableHeader>

                      <TableBody>
                        {sortData(usersDetails, "contributors").map(
                          (row, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <button
                                  onClick={() =>
                                    handleUserClick(
                                      row.user_id,
                                      row.operator_id
                                    )
                                  }
                                  className="truncate px-3 py-1.5 text-xs font-medium rounded-md border border-blue-800 bg-blue-800 text-white hover:bg-gray-200 hover:text-blue-800 transition"
                                >
                                  {row.user}
                                </button>
                              </TableCell>
                              <TableCell className="text-xs">
                                {row.operator_name}
                              </TableCell>
                              <TableCell className="text-xs">
                                {row.turnover}
                              </TableCell>
                              <TableCell className="text-right">
                                {row.margin}
                              </TableCell>
                              <TableCell className="text-right">
                                {row.betcount}
                              </TableCell>
                              <TableCell className="text-red-500 text-right">
                                {" "}
                                {((row.margin * 100) / row.turnover).toFixed(2)}
                                %
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* Row 1, Col 2 */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          {loadingPie ? (
            <div className="text-gray-500 text-center text-sm py-4">
              Loading chart...
            </div>
          ) : (
            <TurnoverByGamePie
              data={turnOverGraph}
              key={turnOverGraph?.length}
            />
          )}
        </div>

        {/* Row 1, Col 3 */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <UsersByOperator
            data={uniqueUserOperator}
            key={uniqueUserOperator?.length}
          />
        </div>

        {/* Row 2, Col 2 */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <TurnoverByOperator
            data={turnOverOperator}
            key={turnOverOperator?.length}
          />
        </div>

        {/* Row 2, Col 3 */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 flex flex-col justify-between w-full min-w-0">
          <ActiveUsersScatter
            activeUser={activeUser}
            key={activeUser?.length}
          />
        </div>
      </div>

      {/* Example chart render */}
    </div>
  );
};

export default Metric;
